import { DefaultFocusStatusModel } from "../../../models/DefaultFocusStatusModel";
import { isChoiceList, QuestionType } from "../../../models/enums/QuestionType";
import { isFocusable } from "../../../models/utilities/defaultFocusStatusUtilities";
import { KeyboardNavigation } from "./KeyboardNavigation";

/**
 * Types that render ChoiceOpenEndRenderer and leave the OE via ButtonChoiceNavigation.
 * Do not fold Carrousel into isChoiceList — that helper also drives ChoiceListItemRenderer
 * role/className and treating Carrousel as a choice-list type would change radio vs checkbox.
 * DropDownList / DrillDown use Select (div[role=combobox]), so the generic loop's
 * tagName === "INPUT" escape hatch does not cover Shift+Tab from their open-ends.
 */
function usesChoiceOpenEndSymmetricShiftTab(type: QuestionType): boolean {
    return (
        isChoiceList(type) ||
        type === QuestionType.Carrousel ||
        type === QuestionType.DropDownList ||
        type === QuestionType.DrillDown
    );
}

export class ButtonChoiceNavigation extends KeyboardNavigation {
    findNextFocusableElementWithinSameVariable(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        const [startIndex, step] = (() => {
            if (eventKey === "ArrowDown" || eventKey === "ArrowRight") {
                return [index + 1, 1];
            } else if (eventKey === "ArrowLeft" || eventKey === "ArrowUp") {
                return [index - 1, -1];
            }
            return [-1, 1];
        })();

        for (let i = startIndex; i >= 0 && i < this.defaultFocusStatus.statuses.length; i += step) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];

            if (
                elementFocusStatus.variableId === currentElement.variableId &&
                elementFocusStatus.elementId !== currentElement.elementId &&
                // Arrows move selection within the radiogroup only — the open-end textarea is
                // not a radio, so it stays Tab-only (see findNextFocusableElementWithinNextVariable).
                !elementFocusStatus.elementId.endsWith("_OE_INPUT") &&
                elementFocusStatus.elementRef?.current &&
                isFocusable(elementFocusStatus)
            ) {
                return elementFocusStatus;
            }
        }

        return null;
    }

    findNextFocusableElementWithinNextVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        // Check if we're in a grid by looking for elements with the same questionId
        const elementsInSameQuestion = this.defaultFocusStatus.statuses.filter(
            (el) => el.questionId === currentElement.questionId && el.questionId !== 0,
        );

        // If we have multiple elements with the same questionId, we're in a grid
        // In that case, Tab should navigate to the next cell within the grid
        if (elementsInSameQuestion.length > 1) {
            for (let i = index + 1; i < this.defaultFocusStatus.statuses.length; i++) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                if (
                    elementFocusStatus.questionId === currentElement.questionId &&
                    elementFocusStatus.elementRef?.current &&
                    isFocusable(elementFocusStatus)
                ) {
                    return this.getFocusableElement(elementFocusStatus, true);
                }
            }
            // If we've reached the end of the grid, move to the next variable
        }

        // Standard behavior for non-grid elements
        for (let i = index + 1, len = this.defaultFocusStatus.statuses.length; i < len; i++) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];

            if (
                (elementFocusStatus.variableId !== 0 &&
                    elementFocusStatus.variableId !== currentElement.variableId &&
                    elementFocusStatus.elementRef?.current &&
                    isFocusable(elementFocusStatus)) ||
                currentElement.elementId.includes("CHECK")
            ) {
                return this.getFocusableElement(elementFocusStatus, true);
            }
        }

        return null;
    }

    findLastFocusableElementWithinPreviousVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        /* DEV-98226: Shift+Tab must mirror Tab for every question type that hosts a choice
         * open-end through ButtonChoiceNavigation (choice lists, carrousel, dropdown,
         * drill-down). Tab steps forward through every element of the question (sibling
         * choices / select and an enabled open-end — see the same-questionId walk in
         * findNextFocusableElementWithinNextVariable), so Shift+Tab steps backward through
         * them the same way before exiting to the previous question. Without this, Shift+Tab
         * from a middle choice or open-end jumps out of the question when an earlier question
         * is still mounted (the generic previous-variable loop returns that question's last
         * live choice and skips the same-grid fallback). */
        if (
            this.question?.type != null &&
            usesChoiceOpenEndSymmetricShiftTab(this.question.type) &&
            !this.isHybridGrid
        ) {
            for (let i = index - 1; i >= 0; i--) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                if (
                    elementFocusStatus.questionId === currentElement.questionId &&
                    elementFocusStatus.questionId !== 0 &&
                    elementFocusStatus.elementRef?.current &&
                    isFocusable(elementFocusStatus)
                ) {
                    return this.getFocusableElement(elementFocusStatus);
                }
            }
            // No earlier element within the question — fall through and exit to the
            // previous variable below.
        }

        for (let i = index - 1; i >= 0; i--) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];

            if (
                elementFocusStatus.variableId !== 0 &&
                elementFocusStatus.elementRef?.current &&
                isFocusable(elementFocusStatus) &&
                (elementFocusStatus.variableId !== currentElement.variableId ||
                    elementFocusStatus.elementRef.current.tagName === "INPUT" ||
                    elementFocusStatus.elementRef.current.tagName === "TEXTAREA" ||
                    currentElement.elementId.includes("CHECK"))
            ) {
                return this.getFocusableElement(elementFocusStatus);
            }
        }

        return null;
    }
}
